import React, { FC, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import Pagination from '../components/navigation/Pagination';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../redux/redux.hooks';
import { getAllShops } from '../reducers/shops.reducer';
import { Shop } from '../models/shop';
import Spacer from '../components/layout/Spacer';
import { useToast } from '../components/toast/ToastProvider';
import { ExistingShopRouteState } from '../route_states/existing.shop.route.state';
import { openShop, shopMatches } from '../utils/open.shop';
import { useFavouriteShops } from '../hooks/use.favourite.shops';
import FavouriteShopButton from '../components/favourite/FavouriteShopButton';

type SearchShopsProps = {
  limit?: number;
  // Renders only the search card, for pages that provide their own s-page.
  embedded?: boolean;
  autoFocus?: boolean;
};

const SearchShops: FC<SearchShopsProps> = ({ limit = 12, embedded = false, autoFocus = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [visibleList, setVisibleList] = useState<any>([]);
  const [query, setQuery] = useState<string | undefined>('');
  const dispatch = useAppDispatch();
  const { data, status } = useAppSelector((state) => state.shopsReducer);
  const toast = useToast();
  const { favourites } = useFavouriteShops();
  const searchFieldRef = useRef<HTMLElementTagNameMap['s-search-field']>(null);

  // s-search-field has no autofocus. Its inner input can exist before the s-* parents
  // are laid out, when focus() silently does nothing, so retry until focus lands.
  useEffect(() => {
    if (!autoFocus || status !== 'successful') {
      return;
    }
    let frame = 0;
    let attempts = 0;
    const focusInput = () => {
      const input = searchFieldRef.current?.shadowRoot?.querySelector('input');
      input?.focus();
      const focused = input && input.getRootNode() instanceof ShadowRoot
        && (input.getRootNode() as ShadowRoot).activeElement === input;
      if (!focused && attempts++ < 120) {
        frame = requestAnimationFrame(focusInput);
      }
    };
    focusInput();
    return () => cancelAnimationFrame(frame);
  }, [autoFocus, status]);

  useEffect(() => {
    if (status === 'error') {
      toast.show('Failed to load shops', { isError: true });
    }
  }, [status, toast]);
  const params = useParams();
  
  const { id: shop_id } = params;

  const handleQueryChange = (value?: string) => {
    setQuery(value);
    if (value) {
      const newList = data.filter((x) => shopMatches(x, value));
      setVisibleList(newList.slice(0, limit - 1));
      setCurrentPage(1);
      setTotalPages(Math.ceil(newList.length / limit));
    }
  };

  const onPreviousClick = () => {
    if (currentPage === 1) {
      return;
    } else {
      let start = currentPage - 2;
      let stop = limit + start;
      setVisibleList(data.slice(start, stop));
      setCurrentPage(currentPage - 1);
    }
  };

  const onNextClick = () => {
    if (currentPage === totalPages) {
      return;
    } else {
      let start = currentPage * limit;
      let stop = limit + start - 1;
      setVisibleList(data.slice(start, stop));
      setCurrentPage(currentPage + 1);
    }
  };

  const renderLoading = () => {
    return (
      <Loader $embedded={embedded}>
        <s-spinner accessibilityLabel='Loading spinner' />
        <Spacer width={12} />
        <s-heading fontSize='large'>Retrieving shops...</s-heading>
      </Loader>
    );
  };

  const renderShop = (item: Shop, key: string) => (
    <li key={key}>
      <ShopButton
        type='button'
        onClick={() => openShop(item, navigate, shop_id)}
      >
        <ShopContainer>
          <h3>
            <s-text fontWeight='semibold'>{item.visible_name && `[${item.visible_name}]`} {item.shop_name}</s-text>
          </h3>
          <Spacer width={24} />
          <s-badge>{item.shop_id}</s-badge>
        </ShopContainer>
      </ShopButton>
      <FavouriteShopButton shopId={item.shop_id} shopName={item.shop_name} />
    </li>
  );

  // Shown above the list while the filter is empty, in the order they were added.
  const renderFavourites = () => {
    if (query) return null;
    const favouriteShops = favourites
      .map((id) => data.find((shop) => shop.shop_id === id))
      .filter((shop): shop is Shop => !!shop);
    if (!favouriteShops.length) return null;
    return (
      <FavouritesSection>
        <s-section padding='none'>
          <SectionHeading id='favourite-shops-heading'>Favourites</SectionHeading>
          <ShopList aria-labelledby='favourite-shops-heading'>
            {favouriteShops.map((item) => renderShop(item, item.shop_id))}
          </ShopList>
        </s-section>
      </FavouritesSection>
    );
  };

  const renderList = () => {
    return (
      <>
      {renderFavourites()}
      <s-section padding='none'>
        <SearchBox>
          <s-search-field
            ref={searchFieldRef}
            label='Filter shops'
            labelAccessibilityVisibility='exclusive'
            placeholder='Filter shops'
            value={query}
            onInput={(event) => { handleQueryChange(event.currentTarget.value) }}
          />
        </SearchBox>
        <ShopList aria-label='Shops'>
          {/* shop_id isn't unique (several test shops share one), so qualify the key. */}
          {visibleList.map((item: Shop, index: number) => renderShop(item, `${item.shop_id}-${index}`))}
        </ShopList>
        <PaginatorWrapper>
          <Pagination
            label={`Page ${currentPage} of ${totalPages}`}
            hasPrevious={currentPage > 1}
            hasNext={currentPage < totalPages}
            onPrevious={onPreviousClick}
            onNext={onNextClick}
          />
        </PaginatorWrapper>
      </s-section>
      </>
    );
  };

  const renderPage = () => {
    if (status === 'loading') {
      return renderLoading();
    } else if (status === 'successful') {
      return renderList();
    }
  };

  useEffect(() => {
    handleQueryChange(searchParams.get('shop') || "")
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      await dispatch(getAllShops());
    })();
  }, [dispatch]);

  useEffect(() => {
    setTotalPages(Math.ceil(data.length / limit));
    setVisibleList(data.slice(0, limit));
    if (query) {
      const newList = data.filter((x) => shopMatches(x, query));
      if (newList.length === 1) {
        navigate(`/existingshop/${newList?.[0].shop_id}/advanced-settings/`, {
          state: {
            shop_name: newList?.[0].shop_name,
            shop_key: newList?.[0].secret_key,
          } as ExistingShopRouteState,
        });
      } else {
        handleQueryChange(query)
      }
    }
  }, [data, limit]);

  if (embedded) {
    return <>{renderPage()}</>;
  }

  return (
    <PageWrapper>
      <s-page heading='Search shops'>
        {renderPage()}
      </s-page>
    </PageWrapper>
  );
};

export default SearchShops;

const PageWrapper = styled.div`
  height: 100%;
  width: 100%;
`;

const Loader = styled.div<{ $embedded: boolean }>`
  display: flex;
  flex: 1;
  margin-top: ${({ $embedded }) => ($embedded ? '0' : '-4em')};
  height: ${({ $embedded }) => ($embedded ? 'auto' : '100vh')};
  padding: ${({ $embedded }) => ($embedded ? '64px 0' : '0')};
  justify-content: center;
  align-items: center;
`;

const ShopContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SearchBox = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e1e3e5;
`;

// Replaces Polaris React's ResourceList: one full-width clickable row per shop.
const ShopList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    padding-right: 8px;
  }

  li:hover {
    background: #f6f6f7;
  }

  li + li {
    border-top: 1px solid #e1e3e5;
  }
`;

const FavouritesSection = styled.div`
  margin-bottom: 16px;
`;

const SectionHeading = styled.h2`
  margin: 0;
  padding: 12px 16px;
  border-bottom: 1px solid #e1e3e5;
  font-size: 13px;
  font-weight: 650;
`;

const ShopButton = styled.button`
  display: block;
  flex: 1;
  min-width: 0;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #005bd3;
    outline-offset: -2px;
  }

  h3 {
    margin: 0;
    font-size: inherit;
  }
`;

const PaginatorWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;
